import React, { useEffect, useState } from 'react';
import { useNode } from '@craftjs/core';
import { POPUP_NODE_NAME } from '../../utils/constants';
import {
  getVisibilityClasses,
  DEFAULT_VISIBILITY,
  getBoxShadow,
  getBorderRadiusStyle,
  getPaddingStyle,
  getMediaQueryHeader,
  getGlobalStyleColor,
} from '../../utils/styleHelpers';
import { useRenderContext } from '../../context/RenderContext';
import { css, cx } from '@emotion/css';
import type { CSSInterpolation, CSSObject } from '@emotion/serialize';
import type { NodeCustomProps } from '../../types/craft';
import { PopupPropsDto } from '@builder4/shared/dto/craft/props/PopupPropsDto';
import type { StylesConfig } from '../../types/styles';

export type PopupProps = PopupPropsDto & {
  children: React.ReactNode;
  onClose?: () => void;
  isOpen?: boolean;
};

export const defaultProps: Partial<PopupProps> = {
  width: '560px',
  height: '320px',
  maxWidth: 1200,
  borderRadius: [8, 8, 8, 8],
  padding: [20, 20, 20, 20],
  popupColor: { r: 255, g: 255, b: 255, a: 1 },
  borderColor: { r: 0, g: 0, b: 0, a: 0 },
  borderWidth: 0,
  overlayColor: { r: 0, g: 0, b: 0, a: 0.5 },
  shadowType: 'none',
  triggers: { click: {} },
  isOpen: true,
  visibility: DEFAULT_VISIBILITY,
  tablet: {},
  mobile: {},
};

const Popup = (props: Partial<PopupProps>) => {
  props = {
    ...defaultProps,
    ...props,
  };

  const {
    id,
    connectors: { connect },
    actions: { setProp },
  } = useNode((node) => ({
    id: node.id,
  }));

  const { isEditable, environment, styles: activeGlobalStyles } = useRenderContext();
  const isServerRender = environment === 'server';
  const isPreview = environment === 'preview';
  const isInspirationPreview = environment === 'inspiration-preview';

  const { children, isOpen, visibility, onClose } = props;

  // Local visibility state for preview mode
  const [previewVisible, setPreviewVisible] = useState(false);

  // Reset preview visibility when entering preview mode
  useEffect(() => {
    if (!isPreview) {
      return;
    }
    setPreviewVisible(false);
  }, [isPreview]);

  // // Delay trigger: show popup after N seconds in preview mode
  useEffect(() => {
    const delayTrigger = props.triggers?.delay;
    if (!isPreview || !delayTrigger) {
      return;
    }

    const delaySeconds = delayTrigger.delay ?? 0;
    const timer = setTimeout(() => setPreviewVisible(true), delaySeconds * 1000);
    return () => clearTimeout(timer);
  }, [isPreview]);

  // Exit-intent trigger: show popup when mouse leaves the document in preview mode
  useEffect(() => {
    const exitTrigger = props.triggers?.exit;
    if (!isPreview || !exitTrigger) {
      return;
    }

    const pageTime = exitTrigger.pageTime ?? 0;
    const handleMouseOut = (e: MouseEvent) => {
      if (e.relatedTarget === null) {
        setPreviewVisible(true);
        document.removeEventListener('mouseout', handleMouseOut);
      }
    };

    const timer = setTimeout(() => {
      document.addEventListener('mouseout', handleMouseOut);
    }, pageTime * 1000);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseout', handleMouseOut);
    };
  }, [isPreview]);

  // Click trigger: show popup when an element with data-popup-target matching this popup is clicked (preview mode)
  useEffect(() => {
    const clickTrigger = props.triggers?.click;
    if (!isPreview || !clickTrigger || !id) {
      return;
    }

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const opener = target?.closest?.(`[data-popup-target="${id}"]`) as HTMLElement | null;
      if (opener) {
        e.preventDefault();
        e.stopPropagation();
        setPreviewVisible(true);
      }
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [isPreview, props.triggers?.click, id]);

  const shouldShow = isPreview ? previewVisible : isOpen;

  // Lock background scroll when popup is visible in preview mode
  useEffect(() => {
    if (!isPreview || !previewVisible) {
      return;
    }

    const scrollY = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      window.scrollTo(0, scrollY);
    };
  }, [isPreview, previewVisible]);

  const visibilityClasses = getVisibilityClasses(visibility || DEFAULT_VISIBILITY);

  const handleOverlayClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isPreview) {
      setPreviewVisible(false);
    } else {
      setProp((props: PopupProps) => {
        props.isOpen = false;
      });
    }
    onClose?.();
  };

  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // Standalone preview mode is used in inspiration page previews, so we don't need to render the popup
  if (isInspirationPreview) {
    return null;
  }

  if (isServerRender) {
    const delayTrigger = props.triggers?.delay;
    const delaySeconds = delayTrigger ? (delayTrigger.delay ?? 0) : undefined;

    const exitTrigger = props.triggers?.exit;
    const exitPageTime = exitTrigger ? (exitTrigger.pageTime ?? 0) : undefined;

    return (
      <dialog
        id={id}
        data-popup-id={id}
        {...(delaySeconds !== undefined && { 'data-trigger-delay': String(delaySeconds) })}
        {...(exitPageTime !== undefined && { 'data-trigger-exit': String(exitPageTime) })}
        className={cx(
          `popup-dialog ${visibilityClasses}`,
          getDialogStyles(props, isServerRender, activeGlobalStyles),
        )}
        style={{ overflow: 'visible' }}
      >
        <CloseButton data-popup-close />
        <div style={{ overflow: 'auto', height: '100%' }}>{children}</div>
      </dialog>
    );
  }

  return (
    <div
      ref={(ref) => {
        if (ref) connect(ref);
      }}
      data-popup-id={id}
      {...(!shouldShow && { style: { display: 'none' } })}
    >
      <div
        className={cx('popup-overlay', getOverlayStyles(props, isServerRender, activeGlobalStyles))}
        onClick={handleOverlayClick}
      >
        <div style={{ position: 'relative' }} onClick={handleModalClick}>
          <CloseButton onClick={handleOverlayClick} />
          <div
            className={cx(
              'popup-modal',
              getModalStyles(props, isServerRender, activeGlobalStyles),
              isEditable && 'node-dashed-indicator boundary-indicator',
            )}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

Popup.craft = {
  displayName: POPUP_NODE_NAME,
  props: defaultProps,
  custom: {
    displayName: POPUP_NODE_NAME,
    isLocked: false,
  } as NodeCustomProps,
};

export default Popup;

const closeButtonStyles = css({
  position: 'absolute' as const,
  top: '-32px',
  right: '0px',
  width: '28px',
  height: '28px',
  borderRadius: '50%',
  background: 'rgba(0,0,0,0.6)',
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  '&:hover': {
    background: 'rgba(0,0,0,0.8)',
  },
  '&:focus': {
    outline: 'none',
  },
});

const CloseButton = ({ onClick, ...rest }: React.ButtonHTMLAttributes<HTMLButtonElement>) => (
  <button className={closeButtonStyles} onClick={onClick} aria-label="Close popup" {...rest}>
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <line x1="2" y1="2" x2="12" y2="12" stroke="white" strokeWidth="2" strokeLinecap="round" />
      <line x1="12" y1="2" x2="2" y2="12" stroke="white" strokeWidth="2" strokeLinecap="round" />
    </svg>
  </button>
);

function getOverlayStyles(
  props: Partial<PopupProps>,
  isServerRender: boolean,
  activeGlobalStyles: StylesConfig,
) {
  const currentBpStyle = computeOverlayStyles(props, activeGlobalStyles);
  const finalStyle: CSSInterpolation = {
    ...(currentBpStyle as CSSObject),
  };

  const { mobile, tablet } = props;
  if (mobile) {
    const mobileStyles = computeOverlayStyles({ ...props, ...mobile }, activeGlobalStyles);
    finalStyle[getMediaQueryHeader(isServerRender, 'mobile')] = mobileStyles as CSSObject;
  }
  if (tablet) {
    const tabletStyles = computeOverlayStyles({ ...props, ...tablet }, activeGlobalStyles);
    finalStyle[getMediaQueryHeader(isServerRender, 'tablet')] = tabletStyles as CSSObject;
  }

  return css(finalStyle);
}

function computeOverlayStyles(props: Partial<PopupProps>, activeGlobalStyles: StylesConfig) {
  const { overlayColor } = props;
  const backgroundColor = getGlobalStyleColor(overlayColor, activeGlobalStyles.colors);

  return {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: backgroundColor,
    zIndex: 1000,
    cursor: 'default',
  };
}

function computePopupStyles(props: Partial<PopupProps>, activeGlobalStyles: StylesConfig) {
  const {
    width,
    height,
    maxWidth,
    borderRadius,
    padding,
    popupColor,
    borderColor,
    borderWidth,
    shadowType,
    shadowSettings,
  } = props;

  const borderRadiusStyle = borderRadius
    ? getBorderRadiusStyle(borderRadius).borderRadius
    : undefined;
  const paddingStyle = padding ? getPaddingStyle(padding).padding : undefined;
  const backgroundColor = getGlobalStyleColor(popupColor, activeGlobalStyles.colors);
  const borderColorStyle = getGlobalStyleColor(borderColor, activeGlobalStyles.colors);

  return {
    width,
    height,
    maxWidth: maxWidth != null ? `${maxWidth}px` : undefined,
    backgroundColor,
    borderRadius: borderRadiusStyle,
    padding: paddingStyle,
    outlineStyle: 'solid' as const,
    outlineWidth: borderWidth ?? 0,
    outlineColor: borderColorStyle,
    boxShadow: getBoxShadow(shadowType, shadowSettings) ?? 'none',
  };
}

function getModalStyles(
  props: Partial<PopupProps>,
  isServerRender: boolean,
  activeGlobalStyles: StylesConfig,
) {
  const currentBpStyle = computeModalStyles(props, activeGlobalStyles);
  const finalStyle: CSSInterpolation = {
    ...(currentBpStyle as CSSObject),
  };

  const { mobile, tablet } = props;
  if (mobile) {
    const mobileStyles = computeModalStyles({ ...props, ...mobile }, activeGlobalStyles);
    finalStyle[getMediaQueryHeader(isServerRender, 'mobile')] = mobileStyles as CSSObject;
  }
  if (tablet) {
    const tabletStyles = computeModalStyles({ ...props, ...tablet }, activeGlobalStyles);
    finalStyle[getMediaQueryHeader(isServerRender, 'tablet')] = tabletStyles as CSSObject;
  }

  return css(finalStyle);
}

function computeModalStyles(props: Partial<PopupProps>, activeGlobalStyles: StylesConfig) {
  const popupStyles = computePopupStyles(props, activeGlobalStyles);

  return {
    ...popupStyles,
    cursor: 'default',
    overflow: 'auto' as const,
    display: 'flex',
    flexDirection: 'column' as const,
  };
}

function computeDialogStyles(props: Partial<PopupProps>, activeGlobalStyles: StylesConfig) {
  const popupStyles = computePopupStyles(props, activeGlobalStyles);
  const { overlayColor } = props;
  const backgroundColor = getGlobalStyleColor(overlayColor, activeGlobalStyles.colors);
  return {
    border: 'none',
    ...popupStyles,
    '&::backdrop': {
      backgroundColor,
      cursor: 'default',
    },
  };
}

function getDialogStyles(
  props: Partial<PopupProps>,
  isServerRender: boolean,
  activeGlobalStyles: StylesConfig,
) {
  const currentBpStyle = computeDialogStyles(props, activeGlobalStyles);
  const finalStyle: CSSInterpolation = {
    ...(currentBpStyle as CSSObject),
  };

  const { mobile, tablet } = props;
  if (mobile) {
    const mobileStyles = computeDialogStyles({ ...props, ...mobile }, activeGlobalStyles);
    finalStyle[getMediaQueryHeader(isServerRender, 'mobile')] = mobileStyles as CSSObject;
  }
  if (tablet) {
    const tabletStyles = computeDialogStyles({ ...props, ...tablet }, activeGlobalStyles);
    finalStyle[getMediaQueryHeader(isServerRender, 'tablet')] = tabletStyles as CSSObject;
  }

  return css(finalStyle);
}
